import { useGetDatasourcesQuery } from 'store/services/helios/dataSources';
import { useGetModulesQuery } from 'store/services/core/modules';
import { useImportFunctionMutation } from 'store/services/helios/functionsService';
import type { ModalsType } from 'types';
import type { InferType } from 'yup';
import type { Option } from 'components/mui/common/Autocomplete';

import { useTranslation } from 'react-i18next';
import { TextField } from 'components/mui/common/TextField';
import { Box, Collapse, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { BooleanWrapper, FormWrapper, SectionTitle } from './FunctionModals.styles';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { ActionModal } from 'components/mui/common/ActionModal';
import { Switch } from 'components/mui/common/Switch';
import { Autocomplete } from 'components/mui/common/Autocomplete';
import { useImportFunctionTemplateModalSchema } from 'utils/validations/helios/useHeliosFunctionsSchema';
import { yupResolver } from '@hookform/resolvers/yup';

import { LoadingContainer } from 'components/common/LoadingContainer';
import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';

const isOption = (val: unknown): val is Option => typeof val === 'object' && val !== null && 'value' in val;

const queryDefaultValues = {
  page: 1,
  pageSize: 100,
  orderBy: 'name' as const,
  orderDesc: false,
  search: '',
  Name: '',
  WsUrlBasic: '',
  WsUrlDomain: '',
  WsUrlGoogle: '',
  WsProfile: '',
  TimeZoneId: '',
};

const defaultValues = {
  functionName: '',
  heliosFunctionId: undefined,
  isCommon: true,
  dataSourceId: '',
  importCodeListTemplates: true,
  moduleId: '',
};

export type ImportFunctionModalSchema = InferType<ReturnType<typeof useImportFunctionTemplateModalSchema>>;

const TRANSLATE_PATH = 'helios.functions.importFunctionModal';

export const ImportFunctionModal: React.FC<ModalsType> = ({ open, onClose }) => {
  const { t, i18n } = useTranslation();
  const importTemplateModalSchema = useImportFunctionTemplateModalSchema();

  const { data: dataSourceData, isLoading } = useGetDatasourcesQuery(queryDefaultValues);
  const { data: modulesData, isLoading: isModulesLoading } = useGetModulesQuery(
    {
      page: 1,
      pageSize: 1000,
      search: '',
      orderBy: 'id',
      orderDesc: false,
    },
    { skip: !open }
  );
  const [importFunction] = useImportFunctionMutation();

  const dataSourceOptions = useMemo(
    () =>
      dataSourceData?.items.map((option) => ({
        value: option.id,
        label: option.name,
      })) ?? [],
    [dataSourceData]
  );

  const modulesOptions = useMemo(
    () =>
      modulesData?.items
        ?.filter((item) => item.isHeliosVisible)
        .map((item) => {
          const language = (i18n.resolvedLanguage ?? i18n.language).toLowerCase().split('-')[0];
          const localizedLabel = language === 'en' ? item.name_En : language === 'sk' ? item.name_Sk : item.name_Cs;
          const label = [localizedLabel, item.name, item.name_Cs, item.name_Sk, item.name_En].find((name) => Boolean(name?.trim())) ?? item.id;
          return { value: item.id, label };
        }) ?? [],
    [modulesData?.items, i18n.language, i18n.resolvedLanguage]
  );

  const {
    control,
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<ImportFunctionModalSchema>({
    defaultValues,
    resolver: yupResolver(importTemplateModalSchema),
  });

  const importCodeListTemplates = useWatch({ control, name: 'importCodeListTemplates' });

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const onSubmit = useCallback(
    async (values: ImportFunctionModalSchema) => {
      try {
        const res = await importFunction({
          functionName: values.functionName,
          heliosFunctionId: values.heliosFunctionId,
          dataSourceId: values.dataSourceId,
          isCommon: Boolean(values.isCommon),
          importCodeListTemplates: Boolean(values.importCodeListTemplates),
          moduleId: values.moduleId || undefined,
        });
        if (!('error' in res)) {
          toast.success(t('helios.functions.toasts.success.import'));
          handleClose();
        } else {
          toast.error(t('helios.functions.toasts.errors.import'));
        }
      } catch (error) {
        toast.error(t('helios.functions.toasts.errors.import'));
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    [importFunction, handleClose, t]
  );

  return (
    <ActionModal
      open={open}
      title={t(`${TRANSLATE_PATH}.title`)}
      onClose={handleClose}
      onCancel={handleClose}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      disableBackdropClick={isSubmitting}
      submitIcon={{
        icon: <AddIcon />,
        position: 'start',
      }}
      cancelText={t(`${TRANSLATE_PATH}.buttons.cancel`)}
      submitText={t(`${TRANSLATE_PATH}.buttons.add`)}>
      <FormWrapper>
        <SectionTitle variant="caption">{t(`${TRANSLATE_PATH}.fields.sections.basicInfo`)}</SectionTitle>
        <Box sx={{ gridColumn: '1 / -1' }}>
          <LoadingContainer loading={isLoading}>
            <Controller
              name="dataSourceId"
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  {...field}
                  options={dataSourceOptions}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  label={t(`${TRANSLATE_PATH}.fields.dataSource`)}
                  placeholder={t('common.select')}
                  onChange={(val) => field.onChange(isOption(val) ? val.value : '')}
                  value={field.value}
                  disableCloseOnSelect={false}
                />
              )}
            />
          </LoadingContainer>
        </Box>

        <Controller
          name="functionName"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
              fullWidth
              label={t(`${TRANSLATE_PATH}.fields.name`)}
            />
          )}
        />
        <Controller
          name="heliosFunctionId"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              type="number"
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
              fullWidth
              label={t(`${TRANSLATE_PATH}.fields.id`)}
            />
          )}
        />
        <SectionTitle variant="caption">{t(`${TRANSLATE_PATH}.fields.sections.importSettings`)}</SectionTitle>
        <BooleanWrapper>
          <Controller
            name="isCommon"
            control={control}
            render={({ field: { value, ref, name, onChange } }) => (
              <Switch
                checked={Boolean(value)}
                onChange={(_e, checked) => onChange(checked)}
                slotProps={{ input: { ref } }}
                name={name}
                label={t(`${TRANSLATE_PATH}.fields.isCommon`)}
              />
            )}
          />
        </BooleanWrapper>
        <Box
          sx={{
            gridColumn: '1 / -1',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
          }}>
          <Controller
            name="importCodeListTemplates"
            control={control}
            render={({ field: { value, ref, name, onChange } }) => (
              <Switch
                checked={Boolean(value)}
                onChange={(_e, checked) => onChange(checked)}
                slotProps={{ input: { ref } }}
                name={name}
                label={t(`${TRANSLATE_PATH}.fields.importCodeListTemplates`)}
              />
            )}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, ml: 1 }}>
            {t(`${TRANSLATE_PATH}.fields.importCodeListTemplatesHelp`)}
          </Typography>
          <Collapse in={importCodeListTemplates}>
            <Box sx={{ mt: 2 }}>
              <LoadingContainer loading={isModulesLoading}>
                <Controller
                  name="moduleId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      {...field}
                      options={modulesOptions}
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                      label={t(`${TRANSLATE_PATH}.fields.module`)}
                      placeholder={t('common.select')}
                      onChange={(val) => field.onChange(isOption(val) ? val.value : '')}
                      value={field.value ?? ''}
                      disableCloseOnSelect={false}
                    />
                  )}
                />
              </LoadingContainer>
            </Box>
          </Collapse>
        </Box>
      </FormWrapper>
    </ActionModal>
  );
};
